/**
 * De vaste onderdelen van een pagina buiten de lades (ontwerp v4): het
 * merk (de h met de apenstaart, in inkt) en de paginakop (.kopt: terug als
 * rond vlak, de titel, rechts hooguit één rond vlak). Inloggen, de
 * voorpagina, de foutschermen, Instellingen en de losse pagina's (tegoed,
 * volgen, plekjes) spreken zo dezelfde taal als Vandaag en Profiel. De vorm
 * staat in heute.css (.merk, .pagina-kop).
 */
import { Link } from 'react-router'
import { useT } from '#app/utils/i18n.tsx'
import { Icon } from './icons.tsx'
import {
	MERK_PADEN,
	MERK_PADEN_DIAPOSITIEF,
	MERK_VIEWBOX,
} from './merk-vorm.ts'

/** Welke vorm: `thema` volgt het thema (in .dark staat het merk licht op
 *  donker en wordt het de iets dunnere diapositieve vorm); `licht` en
 *  `diapositief` zijn vast, voor plekken waar de achtergrond niet met het
 *  thema meedraait (op wit, op een sfeervlak). */
export type MerkVorm = 'thema' | 'licht' | 'diapositief'

const paden = (lijst: ReadonlyArray<string>) =>
	lijst.map((d) => <path key={d} d={d} />)

/** Het merk als beeld, in de tekstkleur, op --size-merk (of -klein). Een
 *  vaste `maat` is er alleen voor de verkleinde voorbeelden (het kaartje,
 *  de telefoon op de voorpagina). */
export function MerkBeeld({
	klein,
	maat,
	vorm = 'thema',
}: {
	klein?: boolean
	maat?: number
	vorm?: MerkVorm
}) {
	return (
		<svg
			className={klein ? 'merk-beeld merk-klein' : 'merk-beeld'}
			viewBox={MERK_VIEWBOX}
			style={maat ? { width: maat, height: maat } : undefined}
			fill="currentColor"
			aria-hidden="true"
			focusable="false"
		>
			{vorm === 'thema' ? (
				<>
					<g className="merk-licht">{paden(MERK_PADEN)}</g>
					<g className="merk-donker">{paden(MERK_PADEN_DIAPOSITIEF)}</g>
				</>
			) : (
				paden(vorm === 'licht' ? MERK_PADEN : MERK_PADEN_DIAPOSITIEF)
			)}
		</svg>
	)
}

/** Het heute-merk. Met `naar` is het de weg terug (een link met een naam
 *  voor de schermlezer, het raakvlak 48 bij 48; `uitlijning` zet het beeld
 *  daarin waar de kop het wil); zonder is het een beeld met de naam heute. */
export function HeuteMerk({
	naar,
	klein,
	uitlijning,
}: {
	naar?: string
	klein?: boolean
	uitlijning?: 'midden' | 'eind'
}) {
	const t = useT()
	if (naar) {
		return (
			<Link
				to={naar}
				className="merk"
				data-uitlijning={uitlijning}
				aria-label={t('merk.naar_begin')}
				data-testid="merk"
			>
				<MerkBeeld klein={klein} />
			</Link>
		)
	}
	return (
		<span className="merk" role="img" aria-label="heute" data-testid="merk">
			<MerkBeeld klein={klein} />
		</span>
	)
}

/** De kop van een pagina: terug (rond vlak), de titel als h1, en rechts
 *  eventueel één ding (een rond vlak, een pil). */
export function PaginaKop({
	titel,
	terug,
	rechts,
	testId,
}: {
	titel: React.ReactNode
	terug?: { naar: string; label: string }
	rechts?: React.ReactNode
	testId?: string
}) {
	return (
		<div className="pagina-kop">
			{terug ? (
				<Link
					to={terug.naar}
					aria-label={terug.label}
					className="prof-rondknop"
					data-testid="pagina-terug"
				>
					<Icon name="chevron-left" size={20} stroke={2.4} />
				</Link>
			) : null}
			<h1 className="pagina-titel" data-testid={testId}>
				{titel}
			</h1>
			{rechts}
		</div>
	)
}
